import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './DecryptedText.css';

const DEFAULT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

function DecryptedText({
  text = '',
  speed = 50,
  trigger = 'mount',
  characters = DEFAULT_CHARS,
  className = '',
  revealDirection = 'start',
}) {
  const [displayText, setDisplayText] = useState('');
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [hasRevealed, setHasRevealed] = useState(false);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);
  const revealedRef = useRef(new Set());
  const observerRef = useRef(null);

  const charArray = useMemo(() => characters.split(''), [characters]);

  const getRandomChar = useCallback(() => {
    return charArray[Math.floor(Math.random() * charArray.length)];
  }, [charArray]);

  const getRevealOrder = useCallback((length) => {
    const indices = Array.from({ length }, (_, i) => i);
    if (revealDirection === 'end') {
      indices.reverse();
    } else if (revealDirection === 'center') {
      const center = Math.floor(length / 2);
      indices.sort((a, b) => Math.abs(a - center) - Math.abs(b - center));
    }
    return indices;
  }, [revealDirection]);

  const startDecryption = useCallback(() => {
    if (isDecrypting || hasRevealed) return;
    setIsDecrypting(true);
    revealedRef.current = new Set();

    const revealOrder = getRevealOrder(text.length);
    let currentIndex = 0;

    // Initial scrambled text
    setDisplayText(
      text.split('').map((ch) => (ch === ' ' ? ' ' : getRandomChar())).join('')
    );

    intervalRef.current = setInterval(() => {
      if (currentIndex >= revealOrder.length) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setDisplayText(text);
        setIsDecrypting(false);
        setHasRevealed(true);
        return;
      }

      const revealIdx = revealOrder[currentIndex];
      revealedRef.current.add(revealIdx);
      currentIndex++;

      setDisplayText(
        text
          .split('')
          .map((ch, i) => {
            if (ch === ' ') return ' ';
            if (revealedRef.current.has(i)) return ch;
            return getRandomChar();
          })
          .join('')
      );
    }, speed);
  }, [text, speed, isDecrypting, hasRevealed, getRandomChar, getRevealOrder]);

  const resetDecryption = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    revealedRef.current = new Set();
    setDisplayText(
      text.split('').map((ch) => (ch === ' ' ? ' ' : getRandomChar())).join('')
    );
    setIsDecrypting(false);
    setHasRevealed(false);
  }, [text, getRandomChar]);

  // Mount trigger
  useEffect(() => {
    if (trigger === 'mount') {
      setDisplayText(
        text.split('').map((ch) => (ch === ' ' ? ' ' : getRandomChar())).join('')
      );
      const timer = setTimeout(startDecryption, 100);
      return () => clearTimeout(timer);
    }
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll trigger via IntersectionObserver
  useEffect(() => {
    if (trigger !== 'scroll') return;
    const el = containerRef.current;
    if (!el) return;

    // Show scrambled placeholder
    setDisplayText(
      text.split('').map((ch) => (ch === ' ' ? ' ' : getRandomChar())).join('')
    );

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startDecryption();
        }
      },
      { threshold: 0.3 }
    );

    observerRef.current.observe(el);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hover trigger: show scrambled text initially
  useEffect(() => {
    if (trigger === 'hover') {
      setDisplayText(
        text.split('').map((ch) => (ch === ' ' ? ' ' : getRandomChar())).join('')
      );
    }
  }, [trigger, text]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      resetDecryption();
      requestAnimationFrame(() => startDecryption());
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover' && isDecrypting) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsDecrypting(false);
    }
  };

  return (
    <span
      ref={containerRef}
      className={`DecryptedText ${className}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={text}
    >
      <span className="DecryptedText__sr-only">{text}</span>
      <span className="DecryptedText__display" aria-hidden="true">
        {displayText.split('').map((char, i) => (
          <span
            key={i}
            className={
              revealedRef.current.has(i)
                ? 'DecryptedText__char DecryptedText__char--revealed'
                : 'DecryptedText__char DecryptedText__char--scrambled'
            }
          >
            {char}
          </span>
        ))}
      </span>
    </span>
  );
}

DecryptedText.defaultProps = {
  text: '',
  speed: 50,
  trigger: 'mount',
  characters: DEFAULT_CHARS,
  className: '',
  revealDirection: 'start',
};

export default DecryptedText;
